'use client';

import { useTranslation } from '@/app/i18n/client';
import { Loader2 } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Form, FormDescription } from '@/components/ui/form';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import { OTPValidatorSchema, generateOTPSchema } from '@/lib/validators/otp';
import { hideMobileNumberDigit } from '@/util/util';
import React, { useRef, useState } from 'react';
import { CountdownTimer } from '@/components/countdown-timer';
import { sendOTP, verifyOTP, verifyOTPResetPassword } from '@/actions';
import toast from 'react-hot-toast';
import {
    borderRadiusShorthand,
    useLayoutStyle,
} from '@/lib/providers/layout-style-provider';
import OTPInputGrid from './otp-input-grid';

interface otpResponseData {
    ref: string;
    transId: string;
}
interface OTPFormProps {
    numDigits?: number;
    type: 'email' | 'mobile';
    value: string;
    displayValue?: string;
    data: otpResponseData;
    onOTPSubmit?: (success: boolean, result?: any) => void;
    useCase?: 'forgot-password';
}

const OTPForm: React.FC<OTPFormProps> = ({
    numDigits = 6,
    type,
    value,
    displayValue,
    data,
    onOTPSubmit,
    useCase,
}) => {
    const { t } = useTranslation();

    const [otp, setOtp] = useState<string[]>(new Array(numDigits).fill(''));
    const inpRefs = useRef(null);
    const [activeBtnSubmit, setActiveBtnSubmit] = useState(false);
    const [activeBtnResendOTP, setActiveBtnResendOTP] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const form = useForm<OTPValidatorSchema>({
        resolver: zodResolver(generateOTPSchema(numDigits)),
    });

    const [otpResponseData, setOtpResponseData] = useState({
        ref: data.ref,
        transId: data.transId,
    });

    const [timerValue, setTimerValue] = useState(180);

    const colorStyle = useContext(ColorStyleContext);
    const layoutStyle = useLayoutStyle();
    const maskedDisplayValue = hideMobileNumberDigit(displayValue || value);

    const handleTimerUpdate = (currentTime: number) => {
        if (currentTime === 0) setActiveBtnResendOTP(true);
        setTimerValue(currentTime);
    };

    const onSubmit = (values: OTPValidatorSchema) => {
        setIsLoading(true);
        const otpValue = Object.values(values).join('');
        if (useCase === 'forgot-password') {
            verifyOTPResetPassword(
                otpResponseData.ref,
                otpValue,
                otpResponseData.transId,
                value,
            )
                .then((res) => {
                    if (res && res.result.success == true) {
                        setIsLoading(false);
                        if (onOTPSubmit) onOTPSubmit(true, res.result);
                    }
                })
                .catch((error: any) => {
                    toast.error(
                        `${t('forgot_password.otp.toast_failed_message')}`,
                        {
                            position: 'bottom-center',
                            duration: 3000,
                        },
                    );
                })
                .finally(() => {
                    setTimeout(() => {
                        setIsLoading(false);
                    }, 3000);
                });
        } else {
            verifyOTP(
                otpResponseData.ref,
                otpValue,
                otpResponseData.transId,
                value,
            )
                .then((res: any) => {
                    if (res && res.result.success == true) {
                        setIsLoading(false);
                        if (onOTPSubmit) onOTPSubmit(true);
                    }
                })
                .catch((error: any) => {
                    toast.error(
                        `${t('forgot_password.otp.toast_failed_message')}`,
                        {
                            position: 'bottom-center',
                            duration: 3000,
                        },
                    );
                })
                .finally(() => {
                    setTimeout(() => {
                        setIsLoading(false);
                    }, 3000);
                });
        }
    };

    const onSubmitResendOTP = () => {
        sendOTP(value).then((res) => {
            setOtpResponseData({
                ref: res.result.ref,
                transId: res.result.transId,
            });
        });
        setTimerValue(60);
        setActiveBtnResendOTP(false);
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                ref={inpRefs}
                className="space-y-4 p-6"
            >
                <FormDescription className="font-light text-sm text-grayc3">
                    {type == 'mobile'
                        ? t('forgot_password.otp.text_sent_mobile_otp', {
                              mobile: maskedDisplayValue,
                          })
                        : t('forgot_password.otp.text_sent_email_otp', {
                              email: maskedDisplayValue,
                          })}
                </FormDescription>
                <OTPInputGrid
                    form={form}
                    numDigits={numDigits}
                    otp={otp}
                    setOtp={setOtp}
                    inpRefs={inpRefs}
                    setActiveBtnSubmit={setActiveBtnSubmit}
                />
                <div className="w-full flex flex-col md:flex-row items-center justify-between gap-5">
                    <button
                        disabled={!activeBtnSubmit || isLoading}
                        type="submit"
                        className={`h-[48px] w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-normal ${
                            activeBtnSubmit ? 'cursor-pointer' : ''
                        } whitespace-nowrap px-4 py-2`}
                        style={
                            {
                                '--dynamic-bg': activeBtnSubmit
                                    ? colorStyle?.buttonBgNormal
                                    : '#f8fafc',
                                '--dynamic-hover-bg': activeBtnSubmit
                                    ? colorStyle?.buttonBgHover
                                    : '#f8fafc',
                                '--dynamic-color': activeBtnSubmit
                                    ? colorStyle?.buttonTextNormal
                                    : '#9397a0',
                                '--dynamic-hover-color': activeBtnSubmit
                                    ? colorStyle?.buttonTextHover
                                    : '#9397a0',
                                '--dynamic-focus-color': activeBtnSubmit
                                    ? colorStyle?.buttonTextHover
                                    : '#9397a0',
                                borderRadius:
                                    borderRadiusShorthand(layoutStyle),
                            } as React.CSSProperties
                        }
                    >
                        <div className="flex items-center justify-center gap-2">
                            {isLoading && (
                                <Loader2 className="animate-spin w-4 h-4" />
                            )}
                            {t('forgot_password.otp.btn_confirm_otp')}
                        </div>
                    </button>
                    <button
                        type="submit"
                        onClick={onSubmitResendOTP}
                        disabled={!activeBtnResendOTP}
                        className={`h-[48px] w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-normal ${
                            activeBtnResendOTP ? 'cursor-pointer' : 'opacity-50'
                        } whitespace-nowrap px-4 py-2`}
                        style={
                            {
                                '--dynamic-bg': activeBtnResendOTP
                                    ? colorStyle?.buttonBgNormal
                                    : '#f8fafc',
                                '--dynamic-hover-bg': activeBtnResendOTP
                                    ? colorStyle?.buttonBgHover
                                    : '#f8fafc',
                                '--dynamic-color': activeBtnResendOTP
                                    ? colorStyle?.buttonTextNormal
                                    : '#9397a0',
                                '--dynamic-hover-color': activeBtnResendOTP
                                    ? colorStyle?.buttonTextHover
                                    : '#9397a0',
                                '--dynamic-focus-color': activeBtnResendOTP
                                    ? colorStyle?.buttonTextHover
                                    : '#9397a0',
                                borderRadius:
                                    borderRadiusShorthand(layoutStyle),
                            } as React.CSSProperties
                        }
                    >
                        {activeBtnResendOTP ? (
                            <>{t('forgot_password.otp.btn_resend_otp')}</>
                        ) : (
                            <>
                                {t('forgot_password.otp.btn_not_receive_otp')}{' '}
                                {timerValue > 0 && (
                                    <CountdownTimer
                                        duration={timerValue}
                                        onTimerUpdate={handleTimerUpdate}
                                    />
                                )}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Form>
    );
};

export default OTPForm;
